import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { CardRow } from '../../../../../../generic/ui/CardRow';
import { Mono } from '../../../../../../generic/ui/Mono';
import { Tag } from '../../../../../../generic/ui/Tag';
import BarcodeIcon from '../../../../../../generic/icons/IconLib/BarcodeIcon';
import UserIcon from '../../../../../../generic/icons/IconLib/UserIcon';
import PencilIcon from '../../../../../../generic/icons/IconLib/PencilIcon';
import { IEntrieDetails } from '../../../../../../modules/API/Knisot/models/entrie';
import { convertToDate } from '../../../../../../modules/convertDateString';
import { stringToTime } from '../../generic/helpers/convert';
import useListsService from '../../../../../../generic/hooks/useListsService';
import WarehouseIcon from '../../../../../../generic/icons/IconLib/WarehouseIcon';
import TruckIcon from '../../../../../../generic/icons/IconLib/TruckIcon';

interface Props {
    data: IEntrieDetails;
    onEdit: () => void;
}

const KnisaTitleCard: React.FC<Props> = ({ data, onEdit }) => {
    const [t] = useTranslation('knisa');
    const { getDesctiptionFromTeuda, getDesctiptionFromWarehouse } = useListsService();

    const desctTeuda = useMemo(
        () => (data.TypeDoc ? getDesctiptionFromTeuda(data.TypeDoc) : ''),
        [data.TypeDoc, getDesctiptionFromTeuda],
    );
    const desctWarehouse = useMemo(
        () => (data.Tranzit ? getDesctiptionFromWarehouse(data.Tranzit) : ''),
        [data.Tranzit, getDesctiptionFromWarehouse],
    );

    const date = data.EntryDate ? convertToDate(data.EntryDate) : '';
    const time = data.EntryTime ? stringToTime(data.EntryTime) : '';
    const grower = (data.GrowerName ?? '').trim();
    const transport = data.Transport ? String(data.Transport) : '';
    const lead = (data.LeadName ?? '').trim();
    const remark = (data.Remark ?? '').trim();
    const reference = data.Reference ? String(data.Reference) : '';

    const hasCar = !!transport || !!lead;
    const hasTeuda = !!desctTeuda.trim();
    const hasWarehouse = !!desctWarehouse.trim();

    return (
        <Card>
            <CardRow
                $columns='5rem minmax(0, 1fr) auto auto'
                $mobileColumns='auto minmax(0, 1fr) auto auto'
                $isLast
                $hover={false}
            >
                <CardRow.Cell>
                    <CardRow.Stack $gap='0.125rem'>
                        <CardRow.Line $gap='0.375rem'>
                            <SmallIcon>
                                <BarcodeIcon color='currentColor' size={16} />
                            </SmallIcon>
                            <Id>{data.EntryNum}</Id>
                        </CardRow.Line>
                        {reference && <Reference>{reference}</Reference>}
                    </CardRow.Stack>
                </CardRow.Cell>

                <CardRow.Cell>
                    <CardRow.Stack $gap='0.375rem'>
                        <CardRow.Line $gap='0.5rem'>
                            <SmallIcon $muted>
                                <UserIcon color='currentColor' size={16} />
                            </SmallIcon>
                            <PrimaryName>{grower}</PrimaryName>
                        </CardRow.Line>
                        {hasWarehouse && (
                            <Tag variant='tinted' color={WAREHOUSE_COLOR}>
                                <ChipInner>
                                    <ChipIcon>
                                        <WarehouseIcon />
                                    </ChipIcon>
                                    <ChipText>{desctWarehouse.trim()}</ChipText>
                                </ChipInner>
                            </Tag>
                        )}
                    </CardRow.Stack>
                </CardRow.Cell>

                <CardRow.Cell $align='center'>
                    <CardRow.Stack $gap='0.125rem' $align='center'>
                        {date && <When>{date}</When>}
                        {time && <TimeText>{time}</TimeText>}
                    </CardRow.Stack>
                </CardRow.Cell>

                <CardRow.Cell $align='center'>
                    <EditBtn type='button' onClick={onEdit} aria-label={t('knisa_create_page_editTitle_aria')}>
                        <PencilIcon color='currentColor' size={16} />
                    </EditBtn>
                </CardRow.Cell>
            </CardRow>

            {hasCar && (
                <CarRow>
                    <NeutralChip>
                        <ChipIcon $muted>
                            <TruckIcon />
                        </ChipIcon>
                        <ChipText>
                            {transport && <Mono>{transport}</Mono>}
                            {transport && lead && <Dot> · </Dot>}
                            {lead && <span>{lead}</span>}
                        </ChipText>
                    </NeutralChip>
                </CarRow>
            )}

            {/* {remark && <RemarkText>{remark}</RemarkText>} */}

            {/* {hasTeuda && (
                <TeudaAnchor>
                    <Tag variant='tinted' color={TEUDA_COLOR}>
                        <ChipInner>
                            <ChipIcon>
                                <BarcodeIcon color='currentColor' size={14} />
                            </ChipIcon>
                            <ChipText>{desctTeuda.trim()}</ChipText>
                        </ChipInner>
                    </Tag>
                </TeudaAnchor>
            )} */}
        </Card>
    );
};

export default KnisaTitleCard;

// Tag colors — distinct hues for warehouse vs teuda-type chips.
// Drawn from generic/ui/colors/groupColorMap palette for visual consistency.
const WAREHOUSE_COLOR = '#0891B2'; // teal — storage/warehouse semantics
const TEUDA_COLOR = '#7C3AED'; // violet — document type semantics

const Card = styled.div`
    position: relative;
    width: 100%;
    border: 1px solid ${({ theme }) => theme.colors.line};
    border-radius: 1rem;
    background: ${({ theme }) => theme.colors.surface};
    overflow: hidden;
`;

const SmallIcon = styled.span<{ $muted?: boolean }>`
    display: inline-flex;
    align-items: center;
    color: ${({ theme, $muted }) => ($muted ? theme.colors.ink3 : theme.colors.ink2)};
    flex-shrink: 0;
`;

const Id = styled(Mono)`
    font-size: ${({ theme }) => theme.text.lg};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.ink};

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`;

const Reference = styled(Mono)`
    font-size: ${({ theme }) => theme.text.sm};
    font-weight: 400;
    color: ${({ theme }) => theme.colors.ink4};

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`;

const PrimaryName = styled.span`
    font-size: ${({ theme }) => theme.text.lg};
    color: ${({ theme }) => theme.colors.ink};
    font-weight: 500;
    white-space: nowrap;
    font-family: ${({ theme }) => theme.fonts.sans};
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`;

const When = styled(Mono)`
    font-size: ${({ theme }) => theme.text.md};
    color: ${({ theme }) => theme.colors.ink2};
    white-space: nowrap;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`;

const TimeText = styled(Mono)`
    font-size: ${({ theme }) => theme.text.sm};
    color: ${({ theme }) => theme.colors.ink3};
    white-space: nowrap;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`;

const EditBtn = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    padding: 0;
    border: 1px solid ${({ theme }) => theme.colors.line};
    background: ${({ theme }) => theme.colors.line2};
    color: ${({ theme }) => theme.colors.ink3};
    cursor: pointer;
    border-radius: 0.5rem;
    transition: all ${({ theme }) => theme.motion.fast} ease;

    svg {
        width: 1rem;
        height: 1rem;
    }

    &:hover {
        background: ${({ theme }) => theme.colors.accent};
        color: ${({ theme }) => theme.colors.ink};
        border-color: ${({ theme }) => theme.colors.accent};
    }

    &:active {
        transform: translateY(1px);
    }
`;

const CarRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem 0.5rem;
    padding: 0 1.25rem 0.5rem;
    direction: rtl;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        padding: 0 0.5rem 0.5rem;
    }
`;

const ChipInner = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    color: inherit;
`;

const NeutralChip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.5rem;
    border-radius: 999px;
    background: ${({ theme }) => theme.colors.line2};
    color: ${({ theme }) => theme.colors.ink2};
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.sm};
    line-height: 1.2;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.sm};
    }
`;

const ChipIcon = styled.span<{ $muted?: boolean }>`
    display: inline-flex;
    align-items: center;
    color: ${({ theme, $muted }) => ($muted ? theme.colors.ink3 : 'inherit')};

    svg {
        width: 0.875rem;
        height: 0.875rem;
    }
`;

const ChipText = styled.span`
    display: inline-flex;
    align-items: baseline;
    gap: 0.125rem;
`;

const Dot = styled.span`
    color: ${({ theme }) => theme.colors.ink4};
`;

const RemarkText = styled.p`
    margin: 0;
    padding: 0 1.25rem 0.5rem;
    /* leave space on the inline-start (physical left in RTL) for the absolute teuda chip */
    padding-inline-start: 7rem;
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.md};
    color: ${({ theme }) => theme.colors.ink3};
    line-height: 1.4;
    direction: rtl;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        padding: 0 0.5rem 0.5rem;
        padding-inline-start: 6rem;
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`;

const TeudaAnchor = styled.div`
    position: absolute;
    bottom: 0;
    inset-inline-end: 0;
    z-index: 1;

    /* Flatten the corner that meets the card edge so the chip sits flush. */
    > * {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        border-end-start-radius: 0;
        border-end-end-radius: 0;
    }
`;
